// src/components/admin/flows/canvas/nodeRegistry.js
import {
    Zap, GitFork, Play, CheckSquare, Clock, Square,
    Bell, Mail, UserPlus, FileText, ListChecks, BarChart3,
    UserCheck, UserMinus, Calendar, Award, AlertCircle, Settings,
    DollarSign, Receipt
} from 'lucide-react';

// ─── Node Type Definitions ──────────────────────────────────────

export const NODE_TYPES = {
    trigger: {
        type: 'trigger',
        label: 'Trigger',
        icon: Zap,
        color: '#22c55e',
        bgClass: 'bg-green-50 dark:bg-green-900/20',
        borderClass: 'border-green-300 dark:border-green-700',
        ringClass: 'ring-green-500',
        description: 'Start the workflow when an event occurs',
        maxOutputs: 1,
        maxInputs: 0,
    },
    condition: {
        type: 'condition',
        label: 'Condition',
        icon: GitFork,
        color: '#f59e0b',
        bgClass: 'bg-amber-50 dark:bg-amber-900/20',
        borderClass: 'border-amber-300 dark:border-amber-700',
        ringClass: 'ring-amber-500',
        description: 'Branch based on a condition',
        maxOutputs: 2, // true & false branches
        maxInputs: 1,
    },
    action: {
        type: 'action',
        label: 'Action',
        icon: Play,
        color: '#3b82f6',
        bgClass: 'bg-blue-50 dark:bg-blue-900/20',
        borderClass: 'border-blue-300 dark:border-blue-700',
        ringClass: 'ring-blue-500',
        description: 'Perform an action (send notification, create task, etc.)',
        maxOutputs: 1,
        maxInputs: 1,
    },
    approval: {
        type: 'approval',
        label: 'Approval',
        icon: CheckSquare,
        color: '#a855f7',
        bgClass: 'bg-purple-50 dark:bg-purple-900/20',
        borderClass: 'border-purple-300 dark:border-purple-700',
        ringClass: 'ring-purple-500',
        description: 'Wait for approval from a user or role',
        maxOutputs: 1,
        maxInputs: 1,
    },
    delay: {
        type: 'delay',
        label: 'Delay',
        icon: Clock,
        color: '#6b7280',
        bgClass: 'bg-gray-50 dark:bg-gray-800',
        borderClass: 'border-gray-300 dark:border-gray-600',
        ringClass: 'ring-gray-500',
        description: 'Wait for a specified duration',
        maxOutputs: 1,
        maxInputs: 1,
    },
    end: {
        type: 'end',
        label: 'End',
        icon: Square,
        color: '#ef4444',
        bgClass: 'bg-red-50 dark:bg-red-900/20',
        borderClass: 'border-red-300 dark:border-red-700',
        ringClass: 'ring-red-500',
        description: 'End the workflow',
        maxOutputs: 0,
        maxInputs: 1,
    },
};

// ─── Trigger Event Options ──────────────────────────────────────

export const TRIGGER_EVENTS = [
    { value: 'employee_added', label: 'Employee Added', icon: UserPlus, description: 'When a new employee is added to the system' },
    { value: 'employee_onboarded', label: 'Employee Onboarded', icon: UserCheck, description: 'When a new employee completes onboarding' },
    { value: 'employee_exit_initiated', label: 'Exit Initiated', icon: UserMinus, description: 'When an employee exit is initiated' },
    { value: 'leave_requested', label: 'Leave Requested', icon: Calendar, description: 'When an employee submits a leave request' },
    { value: 'leave_approved', label: 'Leave Approved', icon: UserCheck, description: 'When a leave request is approved' },
    { value: 'leave_rejected', label: 'Leave Rejected', icon: UserMinus, description: 'When a leave request is rejected' },
    { value: 'attendance_regularization', label: 'Attendance Issue', icon: Clock, description: 'When attendance regularization is needed' },
    { value: 'task_overdue', label: 'Task Overdue', icon: AlertCircle, description: 'When an assigned task passes its due date' },
    { value: 'goal_assigned', label: 'Goal Assigned', icon: Award, description: 'When a performance goal is assigned to an employee' },
    { value: 'probation_ending', label: 'Probation Ending', icon: AlertCircle, description: 'When an employee probation is about to end' },
    { value: 'birthday', label: 'Birthday', icon: Award, description: 'On an employee birthday' },
    { value: 'work_anniversary', label: 'Work Anniversary', icon: Award, description: 'On an employee work anniversary' },
    { value: 'payroll_processed', label: 'Payroll Processed', icon: DollarSign, description: 'When a payroll run is completed for a month' },
    { value: 'manual', label: 'Manual Trigger', icon: Settings, description: 'Manually trigger the workflow' },
    { value: 'scheduled', label: 'Scheduled', icon: Clock, description: 'Run on a schedule (cron)' },
];

// ─── Action Type Options ────────────────────────────────────────

export const ACTION_TYPES = [
    { value: 'create_task', label: 'Create Task', icon: ListChecks, description: 'Create a new task for an employee or team' },
    { value: 'assign_task', label: 'Assign Task', icon: UserPlus, description: 'Assign an existing task to someone' },
    { value: 'send_notification', label: 'Send Notification', icon: Bell, description: 'Send an in-app notification' },
    { value: 'send_email', label: 'Send Email', icon: Mail, description: 'Send an email notification' },
    { value: 'update_employee_field', label: 'Update Employee Field', icon: UserCheck, description: 'Update an employee record field' },
    { value: 'create_document', label: 'Create Document', icon: FileText, description: 'Generate an HR document' },
    { value: 'add_to_checklist', label: 'Add to Checklist', icon: ListChecks, description: 'Add items to onboarding checklist' },
    { value: 'run_report', label: 'Run Report', icon: BarChart3, description: 'Generate and send a report' },
    { value: 'process_payroll', label: 'Process Payroll', icon: DollarSign, description: 'Run payroll for a period (skips locked or already-processed months)' },
    { value: 'generate_payslips', label: 'Generate Payslips', icon: Receipt, description: 'Generate payslip PDFs from the processed payroll' },
];

// ─── Condition Operators ────────────────────────────────────────

export const CONDITION_OPERATORS = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'greater_than', label: 'Greater Than' },
    { value: 'less_than', label: 'Less Than' },
    { value: 'contains', label: 'Contains' },
    { value: 'in', label: 'Is One Of' },
];

// ─── Condition Fields ───────────────────────────────────────────

export const CONDITION_FIELDS = [
    { value: 'employee.department', label: 'Department', type: 'text' },
    { value: 'employee.designation', label: 'Designation', type: 'text' },
    { value: 'employee.employment_type', label: 'Employment Type', type: 'select', options: ['Full Time', 'Part Time', 'Contract', 'Intern'] },
    { value: 'employee.work_location', label: 'Work Location', type: 'text' },
    { value: 'leave.type', label: 'Leave Type', type: 'text' },
    { value: 'leave.days', label: 'Leave Days', type: 'number' },
    { value: 'attendance.hours', label: 'Work Hours', type: 'number' },
    { value: 'employee.tenure_months', label: 'Tenure (months)', type: 'number' },
];

// ─── Default Configs ────────────────────────────────────────────

export const getDefaultConfig = (type) => {
    switch (type) {
        case 'trigger':
            return { event: 'manual', filters: {} };
        case 'condition':
            return { field: 'employee.department', operator: 'equals', value: '' };
        case 'action':
            return { action_type: 'send_notification', params: { title: '', message: '' } };
        case 'approval':
            return {
                approvers: { type: 'reporting_manager', value: '' },
                approval_type: 'any',
                auto_approve_after_hours: null,
                escalate_after_hours: null,
                escalate_to: null,
            };
        case 'delay':
            return { duration_hours: 24, duration_type: 'hours' };
        case 'end':
            return {};
        default:
            return {};
    }
};

// ─── Node Dimensions ────────────────────────────────────────────

export const NODE_WIDTH = 220;
export const NODE_HEIGHT = 72;
export const PORT_RADIUS = 6;
